import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import Utilities from '@/Utility/Utilities';
import LocatorHelper from '@/Utility/LocatorHelper';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';
import LabelViewModel from "@/ViewModels/Controls/LabelViewModel";
import ResourceManager from "@/Management/ResourceManager";

export default class Image extends Control {
    private jQueryControl: JQuery;
    private svgWrapper: JQuery;
    private imgWrapper: JQuery;
    
    public constructor(private viewModel: ImageViewModel, private element?: JQuery<HTMLElement>) {
        super(viewModel, element);
        
        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        if (this.element && this.element[0] instanceof HTMLImageElement)
            console.warn('Image not allowed, please use DIV-Element');

        this.jQueryControl = this.element || $('<div>');
        
        this.svgWrapper = $('<div>').addClass('svg-wrapper');
        this.jQueryControl.append(this.svgWrapper);
        this.imgWrapper = $('<img>').addClass('img-wrapper');
        this.jQueryControl.append(this.imgWrapper);

        this.widget(null);

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget): void {
        this.applyBindings(this.svgWrapper, {
            html: this.createSvgComputed(),
            visible: this.defineComputed(() => {
                return !Utilities.isNullOrUndefined(this.viewModel.svgSource())
                    && Utilities.isNotBlank(this.viewModel.svgSource())
            })
        });

        this.applyBindings(this.imgWrapper, {
            attr: {
                src: this.defineComputed(() => {
                    let source = this.viewModel.convertedSource();
                    if (Utilities.isNullOrUndefined(source))
                        return null;
                    else
                        return source;
                }),
                alt: this.resolveAlternativeText()
            },
            visible: this.defineComputed(() => {
                return (Utilities.isNullOrUndefined(this.viewModel.svgSource())
                    && Utilities.isBlank(this.viewModel.svgSource())
                    && Utilities.isNotBlank(this.viewModel.convertedSource()));
            })
        });

        this.applyBindings(this.jQueryControl, this.getBindingParameter());

        if (!Utilities.isNullOrUndefined(this.viewModel.locator)) {
            this.applyBindings(this.svgWrapper, {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'svg-wrapper')
            });
            this.applyBindings(this.imgWrapper, {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'img-wrapper')
            });
        }
    }

    protected getBindingParameter(): any {
        let tooltip = null;
        if (this.viewModel.tooltipHtmlEnabled) {
            tooltip = {
                caption: this.viewModel.tooltip.caption,
                htmlEnabled: true
            }
        }
        else {
            tooltip = this.viewModel.tooltip;
        }
        return {
            controlName: 'image',
            locator: this.viewModel.locator,
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: tooltip,
            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    if (this.viewModel.isAlwaysVisible() === true) {
                        styles.push('visible');
                    }

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    return states;
                }
            }),
            attr: {
                style: this.defineComputed(() => {
                    if (this.viewModel.isVisible() === true) {
                        return this.viewModel.additionalStyle();
                    } else {
                        return 'display: none;';
                    }
                })
            }
        };
    }
    
    private createSvgComputed(): KnockoutComputed<string> {
        return this.defineComputed(() => {
            const source = this.viewModel.svgSource();
            const alternativeText = this.resolveAlternativeText();

            // Beim initialen durchlauf des Computed ist source == null, was zu einem Parsefehler f�hrt
            if (!source) {
                return source;
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(source, "image/svg+xml");

            const svg = doc.documentElement;

            // Direktes <title>-Kind des Wurzel-SVG entfernen: es loest sonst einen nativen
            // Browser-Tooltip auf der gesamten Icone aus, der zusaetzlich zum App-Tooltip
            // erscheint (doppelter Tooltip). Die Barrierefreiheit bleibt ueber role="img"
            // + aria-label erhalten.
            svg.querySelector(':scope > title')?.remove();
            svg.setAttribute('role', 'img');
            svg.setAttribute('aria-label', alternativeText());

            const serializer = new XMLSerializer();

            return serializer.serializeToString(svg);
        });
    }
    
    private resolveAlternativeText(): KnockoutComputed<string> {
        return this.defineComputed(() => {
            const altText = this.viewModel.alternativeText()
            const src = this.viewModel.source()
            
            if (!Utilities.isNullOrUndefinedOrBlank(altText))
                return altText;

            // falls kein Alternativtext gesetzt wurde: Datenquelle
            if (!Utilities.isNullOrUndefinedOrBlank(src))
                return src;

            // Fallback, falls kein Alternativtext sowie source nicht gesetzt wurde: Unknown
            return ResourceManager.getInstance().get('ARIA_ALT_TEXT_MISSING');
        })
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "AsynchronousBatched";
    }
}

ComponentManager.setControl('Image', Image, 'RADPresentation.HTMLClient.DefaultViewExtensions');